<template>
  <div id="usage">
    <h2 class="sui-header" id="statusHeader">ファイル交換利用状況</h2>
    <div id="date-input">
      <h4 class="month-field">表示月:</h4>
      <div class="date">
        <div id="start-month">
          <div class="field" id="start-month-err" :class="{ 'error': $v.startMonth.$error }">
            <div class="ui icon input">
              <input
                type="text"
                id="startMonthPicker"
                placeholder="start month..."
                v-model.lazy="startMonth"
                @change="changeMonth()"
                @blur="$v.startMonth.$touch()"
                @focus="$v.startMonth.$reset()"
              />
              <i class="calendar alternate outline icon"></i>
            </div>
          </div>
        </div>
        <span class="mark">~</span>
        <div id="end-month">
          <div class="field" id="end-month-err" :class="{ 'error': $v.endMonth.$error }">
            <div class="ui icon input">
              <input
                type="text"
                id="endMonthPicker"
                placeholder="end month..."
                v-model.lazy="endMonth"
                @change="changeMonth()"
                @blur="$v.endMonth.$touch()"
                @focus="$v.endMonth.$reset()"
              />
              <i class="calendar alternate outline icon"></i>
            </div>
          </div>
        </div>
      </div>
      <div class="error start-month-err-msg" v-if="isOrderOfDateError">開始月は終了月よりも前になるようにしてください。</div>
      <div class="error start-month-err-msg" v-if="isOverSpan">表示期間を12か月以内にしてください。</div>
    </div>
    <h3 class="sui-header" id="chart-header">月毎の利用状況</h3>
    <div class="ui form">
      <canvas id="time-usage-chart"></canvas>
      <canvas id="user-usage-chart"></canvas>
    </div>
    <div class="monthly-usage">
      <h3>月毎の送信履歴一覧</h3>
      <table id="monthly-usageTable" class="ui celled table">
        <thead>
          <tr>
            <th>年月</th>
            <th>送付（回）</th>
            <th>引取（回）</th>
            <th>ファイルサイズ(MB)</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in reverseMonthOrder" v-bind:key="item.month">
            <td>{{insertSlashInMonth(item.month)}}</td>
            <td class="right aligned">{{item.transfer.count}}</td>
            <td class="right aligned">{{item.pickup.count}}</td>
            <td
              class="right aligned"
            >{{numberFormat(convertByteToMegaByte(item.transfer.size) + convertByteToMegaByte(item.pickup.size))}}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="user-usage">
      <h3 class="sui-header">人毎の送信履歴一覧</h3>
      <table id="monthly-usageTable" class="ui celled table">
        <thead>
          <tr>
            <th>ユーザ</th>
            <th>送付（回）</th>
            <th>引取（回）</th>
            <th>ファイルサイズ(MB)</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in reverseTotalCountOrder" v-bind:key="item.user_id">
            <td>{{item.user_id}}</td>
            <td class="right aligned">{{item.transfer.count}}</td>
            <td class="right aligned">{{item.pickup.count}}</td>
            <td
              class="right aligned"
            >{{numberFormat(convertByteToMegaByte(item.transfer.size) + convertByteToMegaByte(item.pickup.size))}}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
<script>
import { required } from 'vuelidate/lib/validators'
import Chart from 'chart.js'
import flatpickr from 'flatpickr'
import monthSelectPlugin from 'flatpickr/dist/plugins/monthSelect/index.js'
import { getExchangeUsage } from '@/api'
import { Japanese } from 'flatpickr/dist/l10n/ja.js'
import moment from 'moment'
import numeral from 'numeral'
/**
 * declare year month format
 */
const yearMonthFormat = 'YYYY/MM'

/**
 * declare chart style of pickup
 */
const pickupLabel = '送付'
const pickupBackgroundColor = 'rgba(46, 44, 211, 0.7)'
const pickupHighlightFill = 'rgba(220,220,220,0.75)'
const pickupHighlightStroke = 'rgba(220,220,220,1)'

/**
 * declare chart style for transfer
 */
const transferLable = '引取'
const transferBackgroundColor = 'rgba(243, 120, 32, 1)'
const transferHighlightFill = 'rgba(0,0,0,0.5)'
const transferHighlightStroke = 'rgba(0,0,0,0.5)'

/**
 * to convert to string with comma and 1 decimal value
 */
const numberFormat = (number) => {
  return numeral(number).format('0,0.0')
}

/**
 * declare chart options
 */
const options = {
  responsive: true,
  tooltips: {
    callbacks: {
      label: function (tooltipItem, data) {
        var value = data.datasets[0].data[tooltipItem.index]
        return numberFormat(value)
      }
    }
  },
  legend: {
    display: true,
    onClick: e => e.stopPropagation(),
    labels: {
      padding: 50
    }
  },
  scales: {
    xAxes: [
      {
        stacked: true,
        barPercentage: 0.8,
        maxBarThickness: 60,
        minBarLength: 8,
        gridLines: {
          display: false
        }
      }
    ],
    yAxes: [
      {
        stacked: true,
        ticks: {
          beginAtZero: true,
          userCallback: function (value, index, values) {
            return numberFormat(value)
          }
        },
        scaleLabel: {
          display: true,
          labelString: ''
        }
      }
    ]
  }
}

export default {
  data: function () {
    return {
      timeUsageChart: Chart,
      userUsageChart: Chart,
      startMonth: '',
      endMonth: '',
      monthlyUsage: [],
      userUsage: []
    }
  },
  validations: {
    startMonth: {
      required
    },
    endMonth: {
      required
    }
  },
  computed: {
    reverseMonthOrder: function () {
      return this.monthlyUsage
        .slice()
        .sort((a, b) =>
          moment(this.insertSlashInMonth(b.month), yearMonthFormat).diff(
            moment(this.insertSlashInMonth(a.month), yearMonthFormat)
          )
        )
    },
    reverseTotalCountOrder: function () {
      return this.userUsage
        .slice()
        .sort(
          (a, b) =>
            b.transfer.count +
            b.pickup.count -
            (a.transfer.count + a.pickup.count)
        )
    },
    isOrderOfDateError: function () {
      if (!this.startMonth || !this.endMonth ||
        moment(this.endMonth, yearMonthFormat).isBefore(moment(this.startMonth, yearMonthFormat))) {
        return true
      }
      return false
    },
    isOverSpan: function () {
      if (moment(this.endMonth, yearMonthFormat).diff(moment(this.startMonth, yearMonthFormat), 'years') >= 1) {
        return true
      }
      return false
    }
  },
  mounted () {
    this.initializeMonthPicker('#startMonthPicker')
    this.initializeMonthPicker('#endMonthPicker')
    this.showUsage(this.startMonth, this.endMonth)
  },
  created () {
    this.startMonth = this.getInitialstartMonth()
    this.endMonth = this.getInitialendMonth()
  },
  methods: {
    /**
     * retrieve and show usage data when month was changed
     */
    changeMonth: function () {
      if (!this.isOrderOfDateError && !this.isOverSpan) {
        this.updateUsage(this.startMonth, this.endMonth)
      }
    },
    /**
     * generate initial end month
     */
    getInitialendMonth () {
      return moment().format(yearMonthFormat)
    },
    /**
     * generate 3 months ago of current month
     */
    getInitialstartMonth () {
      return moment()
        .subtract(2, 'months')
        .format(yearMonthFormat)
    },
    /**
     * to show the data to user
     */
    showUsage: function (startMonth, endMonth) {
      this.$store.commit('progress/setLoading', true)
      /**
       * to get usage data from server
       */
      // change date format from "yyyy/MM" to "yyyyMM"
      getExchangeUsage(startMonth.replace('/', ''), endMonth.replace('/', ''))
        .then(response => {
          this.monthlyUsage = this.formatResponseData(response.monthly_usage)
          this.userUsage = this.formatResponseData(response.user_usage)

          const result = this.generateTotalTimeUsage(this.monthlyUsage)
          const timeUsageData = {
            labels: result.labels,
            datasets: [
              {
                label: pickupLabel,
                backgroundColor: pickupBackgroundColor,
                highlightFill: pickupHighlightFill,
                highlightStroke: pickupHighlightStroke,
                data: result.transferTimes
              },
              {
                label: transferLable,
                backgroundColor: transferBackgroundColor,
                highlightFill: transferHighlightFill,
                highlightStroke: transferHighlightStroke,
                data: result.pickupTimes
              }
            ]
          }
          options.scales.yAxes[0].scaleLabel.labelString = '交換回数'
          const totalTimeChartData = {
            type: 'bar',
            data: timeUsageData,
            options: options
          }

          this.timeUsageChart = this.createUsageChart(
            'time-usage-chart',
            totalTimeChartData
          )

          const sizeUsageData = {
            labels: result.labels,
            datasets: [
              {
                label: pickupLabel,
                backgroundColor: pickupBackgroundColor,
                highlightFill: pickupHighlightFill,
                highlightStroke: pickupHighlightStroke,
                data: result.transferSizes
              },
              {
                label: transferLable,
                backgroundColor: transferBackgroundColor,
                highlightFill: transferHighlightFill,
                highlightStroke: transferHighlightStroke,
                data: result.pickupSizes
              }
            ]
          }
          options.scales.yAxes[0].scaleLabel.labelString = 'ファイルサイズ(MB)'
          const totalSizeChartData = {
            type: 'bar',
            data: sizeUsageData,
            options: options
          }
          this.userUsageChart = this.createUsageChart(
            'user-usage-chart',
            totalSizeChartData
          )
          this.$store.commit('progress/setLoading', false)
        })
        .catch(err => {
          console.log(err)
          this.$store.commit('progress/setLoading', false)
          this.$store.commit('infoModal/openModal', {
            type: 'ERROR',
            title: '利用状況',
            message:
              '通信エラーが発生しました。\nしばらくしてからもう一度お試しください。'
          })
        })
    },
    /**
     * update the chart
     */
    updateUsage: function (startMonth, endMonth) {
      this.$store.commit('progress/setLoading', true)
      getExchangeUsage(startMonth.replace('/', ''), endMonth.replace('/', ''))
        .then(response => {
          this.monthlyUsage = this.formatResponseData(response.monthly_usage)
          this.userUsage = this.formatResponseData(response.user_usage)

          const result = this.generateTotalTimeUsage(this.monthlyUsage)

          this.timeUsageChart.data.datasets[0].data = result.transferTimes
          this.timeUsageChart.data.datasets[1].data = result.pickupTimes
          this.timeUsageChart.data.labels = result.labels
          this.timeUsageChart.update()

          this.userUsageChart.data.datasets[0].data = result.transferSizes
          this.userUsageChart.data.datasets[1].data = result.pickupSizes
          this.userUsageChart.data.labels = result.labels
          this.userUsageChart.update()
          this.$store.commit('progress/setLoading', false)
        })
        .catch(err => {
          console.log(err)
          this.$store.commit('progress/setLoading', false)
          this.$store.commit('infoModal/openModal', {
            type: 'ERROR',
            title: '利用状況',
            message:
              '通信エラーが発生しました。\nしばらくしてからもう一度お試しください。'
          })
        })
    },
    /**
     * calculate total time usage
     */
    generateTotalTimeUsage (usageList) {
      let transferTimes = []
      let pickupTimes = []
      let transferSizes = []
      let pickupSizes = []
      let labels = []
      usageList.forEach(element => {
        transferTimes.push(element.transfer.count || 0)
        pickupTimes.push(element.pickup.count || 0)
        transferSizes.push(
          this.convertByteToMegaByte(element.transfer.size)
        )
        pickupSizes.push(this.convertByteToMegaByte(element.pickup.size))
        labels.push(this.insertSlashInMonth(element.month))
      })
      return {
        transferTimes: transferTimes,
        pickupTimes: pickupTimes,
        transferSizes: transferSizes,
        pickupSizes: pickupSizes,
        labels: labels
      }
    },
    /**
     * create chart
     */
    createUsageChart (chartId, chartData) {
      const ctx = document.getElementById(chartId)
      const chart = new Chart(ctx, {
        type: chartData.type,
        data: chartData.data,
        options: chartData.options
      })
      return chart
    },
    /**
     * to convert byte to megabyte and 1 decimal digit
     */
    convertByteToMegaByte (dataSize) {
      if (dataSize) {
        return Math.round(dataSize * 10 / (1024 * 1024)) / 10
      } else {
        return 0.0
      }
    },
    /**
     * to convert to string with comma and 1 decimal value
     */
    numberFormat,
    /**
     * to format the response data of monthly usage and user usage
     */
    formatResponseData (responseData) {
      let dataList = []
      for (let response of responseData) {
        if (response.month) {
          dataList.push(response)
        }
      }
      return dataList
    },
    /**
     * insert slash in between year and month
     */
    insertSlashInMonth (month) {
      return month.slice(0, 4) + '/' + month.slice(4)
    },
    /**
     * initialize month picker
     */
    initializeMonthPicker (filedId) {
      flatpickr(filedId, {
        locale: Japanese,
        plugins: [
          /* As plugin name cannot be changed, suppression is needed */
          /* eslint new-cap: 0 */
          new monthSelectPlugin({
            shorthand: true,
            dateFormat: 'Y/m',
            altFormat: 'F Y',
            theme: 'light'
          })
        ],
        onChange: function (selectedDates, dateStr, instance) {
          if (dateStr && dateStr !== '') {
            instance.close()
          }
        }
      })
    }
  }
}
</script>
<style>
#usage {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
}
h3#chart-header {
  max-width: 600px;
  margin: auto;
}
#date-input {
  max-width: 600px;
  margin: auto;
  width: 100%;
  margin-bottom: 50px;
  margin-top: 45px;
}
#statusHeader {
  max-width: 600px;
  margin: auto;
  margin-bottom: 20px;
}
.month-field {
  float: left;
  margin-right: 25px;
  padding-top: 10px;
  margin-left: 20px;
}
#planet-chart {
  margin-bottom: 30px;
}
.mark {
  float: left;
  margin-left: 25px;
  margin-right: 25px;
  margin-top: 10px;
}
#start-month,
#end-month {
  float: left;
}
.date {
  width: 600px;
  height: 50px;
}
#start-month-err,
#end-month-err {
  margin-top: 0px;
}
.ui.icon.input {
  width: 220px;
}
.monthly-usage,
.user-usage {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  margin-top: 50px;
}
.start-month-err-msg {
  margin-top: 0px;
  padding-left: 95px;
}
th,
td {
  white-space: nowrap;
}
input#startMonthPicker,
input#endMonthPicker {
  text-align: center;
}
</style>
